import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Anchor,
  AppButton,
  Box,
  FlatTextInput,
  Form,
  getValidationResolver,
  Preloader,
  RawTextInput,
  SubmitHandler,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { REGEXPS } from '@/common';
import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import { useHistory } from '@/modules/core/hooks';
import { AccountLockoutModal } from '@/modules/user/components/SignIn/MultiFactorAuthentication/components';
import {
  MFAChannel,
  MFAChannelType,
  MfaServerErrorText,
} from '@/modules/user/components/SignIn/MultiFactorAuthentication/mfa.types';
import MultiFactorAuthenticationModals from '@/modules/user/components/SignIn/MultiFactorAuthentication/MultiFactorAuthenticationModals';
import useMFAStore from '@/modules/user/components/SignIn/MultiFactorAuthentication/stores/useMFA.store';
import { SignInErrorCode, SignInServerErrorText } from '@/modules/user/components/SignIn/signIn.types';

import usePartnerLoginConfig from './hooks/usePartnerLoginConfig';
import useSignIn, { LoginPayload } from './hooks/useSignIn';

interface SignInProps {
  children?: React.ReactNode;
  disabled?: boolean;
  isChangePasswordMode?: boolean;
  onForgotPassword?: () => void;
  onForgotUsername?: () => void;
}

const REQUIRED_FIELD = 'This field is required';
const ACCOUNT_LOCKED_MINIMUM_TIME = 1;

const getDefaultChannel = (channels: MFAChannel[]) =>
  channels?.find((channel) => channel.type === MFAChannelType.SMS) || channels?.[0];

const SignInForm: React.FC<SignInProps> = ({
  children,
  isChangePasswordMode,
  onForgotPassword,
  onForgotUsername,
  disabled,
}) => {
  const { isMobile } = useResponsive();
  const history = useHistory();
  const { onLogin, onChangePassword } = useSignIn();
  const [isMFAModalVisible, setIsMFAModalVisible] = useState(false);
  const [isAccountLockedModalVisible, setIsAccountLockedModalVisible] = useState(false);
  const [lockingLeft, setLockingLeft] = useState<number>();
  const [isSingInError, setIsSingInError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const { setActiveChannel, setChannels, setSession, setUserName, setIsRequiredExpressSetup } = useMFAStore();
  const { partnerLoginConfig } = usePartnerLoginConfig();

  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<LoginPayload>({
    resolver: getValidationResolver({
      username: yup.string().required(REQUIRED_FIELD),
      password: yup.string().required(REQUIRED_FIELD),
      ...(isChangePasswordMode
        ? {
            newPassword: yup
              .string()
              .test('len', 'Use a complex password', (val) =>
                Boolean(val && REGEXPS.PASSWORD_COMPLEXITY.test(val))
              ),
          }
        : {}),
    }),
  });

  const passwordLabel = isChangePasswordMode ? 'Old password' : 'Password';
  const submitTitle = isChangePasswordMode ? 'Change Password' : 'Sign In';

  const handleShowAccountLockedModal = useCallback((lockingLeft: number) => {
    setIsMFAModalVisible(false);
    setIsAccountLockedModalVisible(true);
    setLockingLeft(lockingLeft || ACCOUNT_LOCKED_MINIMUM_TIME);
  }, []);

  const handleLogin = useCallback(
    async (loginPayload: LoginPayload) => {
      if (disabled) {
        return;
      }

      setErrorText(undefined);
      try {
        setIsLoading(true);
        const authResponse = await onLogin(loginPayload);
        const channels = authResponse?.channels;
        if (channels?.length) {
          const defaultChannel = getDefaultChannel(channels);
          setActiveChannel(defaultChannel);
          setChannels(channels);
          setSession(authResponse?.session || '');
          setUserName(loginPayload?.username);
          setIsRequiredExpressSetup(authResponse?.requiredExpressSetup);
          setIsMFAModalVisible(true);
        }
        setIsSingInError(false);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);

        const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
        const lockingLeft = _.get(error, 'response.data.locking_left', 10) as number;
        const authErrorCode = _.get(error, 'response.data.auth_error_code', '') as string;

        if (errorMessage.includes(MfaServerErrorText.USER_ACCOUNT_IS_LOCKED)) {
          handleShowAccountLockedModal(lockingLeft);
          setIsSingInError(false);
          return;
        }

        if (authErrorCode === SignInErrorCode.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED) {
          history.push(ROUTES.CHANGE_PASSWORD);
          setIsSingInError(false);
          return;
        }

        if (authErrorCode === SignInErrorCode.ATTEMPTS_LIMIT_ERROR_CODE) {
          setErrorText('Too many failed login attempts. Please try again in a few minutes');
          setIsSingInError(false);
          return;
        }

        if (errorMessage.includes(SignInServerErrorText.CREDENTIALS_ARE_INCORRECT)) {
          setErrorText('Username or password is wrong');
          setIsSingInError(false);
          return;
        }

        setIsSingInError(true);
      }
    },
    [
      disabled,
      handleShowAccountLockedModal,
      onLogin,
      setActiveChannel,
      setChannels,
      setSession,
      setUserName,
      setIsRequiredExpressSetup,
      history,
    ]
  );

  const handleChangePassword = useCallback(
    async (loginPayload: LoginPayload) => {
      try {
        setErrorText(undefined);
        const authResponse = await onChangePassword(loginPayload);
        const channels = authResponse?.channels;
        if (channels?.length) {
          const defaultChannel = getDefaultChannel(channels);
          setActiveChannel(defaultChannel);
          setChannels(channels);
          setSession(authResponse?.session || '');
          setUserName(loginPayload?.username);
          setIsRequiredExpressSetup(authResponse?.requiredExpressSetup);
          setIsMFAModalVisible(true);
        }
        setIsSingInError(false);
      } catch (error) {
        const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

        if (errorMessage.includes(SignInServerErrorText.CREDENTIALS_ARE_INCORRECT)) {
          setErrorText('Username or password is wrong');
          return;
        }

        setIsSingInError(true);
      }
    },
    [onChangePassword, setActiveChannel, setChannels, setIsRequiredExpressSetup, setSession, setUserName]
  );

  const onSubmit: SubmitHandler<LoginPayload> = useCallback(
    (loginPayload) => {
      if (isChangePasswordMode) {
        handleChangePassword(loginPayload);
        return;
      }

      handleLogin(loginPayload);
    },
    [isChangePasswordMode, handleChangePassword, handleLogin]
  );

  if (partnerLoginConfig) {
    return (
      <Box width={{ width: '100%', max: '220px' }} alignSelf="center">
        <AppButton
          disabled={disabled}
          width="100%"
          className="button"
          type="submit"
          size="L"
          onClick={() => {
            window.location.href = partnerLoginConfig.loginUrl;
          }}
        >
          {partnerLoginConfig.buttonText}
        </AppButton>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box direction="column">
            <Controller
              // @ts-ignore
              name="username"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box direction="column" margin={{ bottom: 'spacing24' }}>
                  <RawTextInput
                    name="username"
                    flex
                    testId="auth_username"
                    label="Username"
                    isLarge
                    placeholder="Username"
                    value={value}
                    onChange={onChange}
                    onBlur={() => {
                      onChange(value?.trim());
                    }}
                    error={!!errors.username?.message || !!errorText}
                    errorWithoutText
                    isLowerCaseMode
                  />
                </Box>
              )}
            />

            <Text size="large" weight="bold" margin={{ bottom: 'spacing12' }} style={{ lineHeight: '24px' }}>
              Password
            </Text>
            <Controller
              // @ts-ignore
              name="password"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box direction="column" margin={{ bottom: 'spacing24' }}>
                  <FlatTextInput
                    label={passwordLabel}
                    placeholder={passwordLabel}
                    inputType="password"
                    testId="auth_password_input"
                    isLarge
                    value={value}
                    onChange={onChange}
                    name="password"
                    errorText={errors.password?.message || errorText}
                  />
                </Box>
              )}
            />

            {isChangePasswordMode && (
              <Controller
                // @ts-ignore
                name="newPassword"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Box direction="column" margin={{ bottom: 'spacing24' }}>
                    <FlatTextInput
                      label="New password"
                      placeholder="New password"
                      inputType="password"
                      testId="auth_change-password_input"
                      isLarge
                      passwordHelper
                      passwordHelperLeftPosition
                      value={value}
                      onChange={onChange}
                      name="newPassword"
                      errorText={(errors.newPassword?.message as string) || errorText}
                    />
                  </Box>
                )}
              />
            )}
            {children}
            <AppButton
              disabled={disabled || isLoading}
              testId="auth_login"
              type="submit"
              width="100%"
              size="L"
            >
              {isLoading ? <Preloader color="white" /> : submitTitle}
            </AppButton>
            {/* <Box margin={{ top: 'spacing24' }}>
              <Text size="small">
                By clicking Sign In, you agree to
                {' '}
                <Anchor color="brand" size="small" weight="normal">
                  Elevate's Terms and Conditions
                </Anchor>
                {' '}
                and have read and acknowledged our
                {' '}
                <Anchor color="brand" size="small" weight="normal">
                  Privacy Policy.
                </Anchor>
              </Text>
            </Box> */}
            <Box direction="row" margin={{ top: 'spacing24' }} align="center" justify="center">
              <Anchor
                size={isMobile ? 'medium' : 'large'}
                weight="bold"
                disabled={disabled}
                data-testid="auth_forgot-username_button"
                onClick={onForgotUsername}
                margin={{ horizontal: 'spacing12' }}
              >
                Forgot Username?
              </Anchor>
              <Anchor
                size={isMobile ? 'medium' : 'large'}
                data-testid="auth_forgot-password_button"
                weight="bold"
                disabled={disabled}
                onClick={onForgotPassword}
                margin={{ horizontal: 'spacing12' }}
                style={{
                  lineHeight: '22xp',
                }}
              >
                Forgot Password?
              </Anchor>
              {/* <Box width="4px" height="4px" background="border1" style={{ borderRadius: 4 }} /> */}
            </Box>
            {/* <Text size="large" margin={{ top: 'spacing12' }} textAlign="center">
              New to Elevate?
              {' '}

              <Anchor
                href="/user/sign-up"
                color="brand"
                size="large"
                weight="bold"
              >
                Register
              </Anchor>
            </Text> */}
          </Box>
        </Form>
      </Box>

      {isMFAModalVisible && (
        <MultiFactorAuthenticationModals
          onSetVisible={setIsMFAModalVisible}
          onShowAccountLockedModal={handleShowAccountLockedModal}
        />
      )}
      {isAccountLockedModalVisible && (
        <AccountLockoutModal onSetVisible={setIsAccountLockedModalVisible} lockingLeft={lockingLeft} />
      )}
      {isSingInError && (
        <WarnModal
          testId="sign-in_error"
          visible={isSingInError}
          onSetVisible={setIsSingInError}
          header="The server encountered an error processing the request"
          helptext="Some technical issues occur during login. Check your credentials and try again."
          buttonText="Close"
          submitButtonText="Try Again"
          onSubmit={handleSubmit(onSubmit)}
          isAwaitMode
          disableAutomaticClosing
        />
      )}
    </>
  );
};

export default SignInForm;
